// Identity in localStorage (Tech §6.1): a random token for rejoining as a guest, plus the
// session of a logged-in account. The server picks the name: account name, or "Gast1234"
// derived from the token.

const TOKEN_KEY = 'webtanks.token';
const SESSION_KEY = 'webtanks.session';

/** 16 random bytes, created on first use. */
export function token(): Uint8Array {
  const hex = localStorage.getItem(TOKEN_KEY);
  if (hex && /^[0-9a-f]{32}$/.test(hex)) {
    return Uint8Array.from(hex.match(/../g)!, (h) => parseInt(h, 16));
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16)); // works without HTTPS, unlike randomUUID
  localStorage.setItem(TOKEN_KEY, [...bytes].map((b) => b.toString(16).padStart(2, '0')).join(''));
  return bytes;
}

/** The guest name the server gives this browser (same formula as `server::db::guest_name`). */
export function guestName(): string {
  const t = token();
  return `Guest${1000 + ((t[0] | (t[1] << 8)) % 9000)}`;
}

/** Session of the logged-in account, '' for guests. */
export function session(): string {
  return localStorage.getItem(SESSION_KEY) ?? '';
}

/** Cosmetic in the shop catalog (`server::looks`). */
export interface Item {
  id: number;
  kind: number;
  name: string;
  price: number;
}

export interface Profile {
  name: string;
  points: number;
  /** Rating and leaderboard place, null before enough matches (Design §11.3). */
  rating: number;
  place: number | null;
  owned: number[];
  /** Equipped item per kind, 0 = standard. */
  looks: number[];
  /** Emote ids on the keys 1–4 (Design §11.4). */
  emotes: number[];
  catalog: Item[];
}

/** Account behind the session; undefined for guests (an expired session is dropped). */
export async function profile(): Promise<Profile | undefined> {
  if (!session()) return undefined;
  const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${session()}` } });
  if (res.status === 401) localStorage.removeItem(SESSION_KEY);
  return res.ok ? res.json() : undefined;
}

/** The cosmetics shop, empty if the server cannot be reached. */
export async function catalog(): Promise<Item[]> {
  try {
    const res = await fetch('/api/catalog');
    return res.ok ? await res.json() : [];
  } catch {
    return [];
  }
}

/** Logs in or registers. Returns an error code (bad_name, bad_password, name_taken, wrong) or ''. */
export async function signIn(register: boolean, name: string, password: string): Promise<string> {
  const res = await fetch(register ? '/api/register' : '/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, password }),
  });
  const body = await res.json();
  if (!res.ok) return body.error ?? 'wrong';
  localStorage.setItem(SESSION_KEY, body.session);
  return '';
}

export async function signOut(): Promise<void> {
  const s = session();
  localStorage.removeItem(SESSION_KEY);
  await fetch('/api/logout', { method: 'POST', headers: { Authorization: `Bearer ${s}` } }).catch(() => {});
}

/**
 * Buys (`buy`, body `{item}`) or equips (`equip`, body `{kind, item}`) a cosmetic. The updated
 * profile, or an error code (unknown, owned, points, not_owned, session).
 */
export async function shop(action: 'buy' | 'equip', body: object): Promise<Profile | string> {
  const res = await fetch(`/api/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session()}` },
    body: JSON.stringify(body),
  });
  if (res.status === 401) return 'session';
  const json = await res.json();
  return res.ok ? json : (json.error ?? 'unknown');
}
